import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import type { Property } from '@/lib/types';
import { Sidebar, type StatusFilter } from '@/components/features/Sidebar';
import { Topbar } from '@/components/features/Topbar';
import { MetricCards } from '@/components/features/MetricCards';
import { PropertyTable } from '@/components/features/PropertyTable';
import { PropertyCards } from '@/components/features/PropertyCards';
import { Pagination } from '@/components/features/Pagination';
import { PropertyForm } from '@/components/features/PropertyForm';
import { ChatWindow } from '@/components/features/ChatWindow';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

// 简单的 debounce hook
function useDebounced<T>(value: T, delay = 300): T {
	const [v, setV] = useState(value);
	useEffect(() => {
		const t = setTimeout(() => setV(value), delay);
		return () => clearTimeout(t);
	}, [value, delay]);
	return v;
}

export default function App() {
	const qc = useQueryClient();

	// 查询参数
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const [search, setSearch] = useState('');
	const [statusFilter, setStatusFilter] = useState<StatusFilter>('');
	const [activeFilter, setActiveFilter] = useState('');
	const debouncedSearch = useDebounced(search, 350);

	// UI 状态
	const [selected, setSelected] = useState<Set<number>>(new Set());
	const [editing, setEditing] = useState<Property | null>(null);
	const [formOpen, setFormOpen] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState<Property | 'batch' | null>(null);
	const [chatOpen, setChatOpen] = useState(false);
	const [sidebarOpen, setSidebarOpen] = useState(false);

	useEffect(() => { setPage(1); }, [debouncedSearch, statusFilter, activeFilter, pageSize]);

	const { data, isLoading } = useQuery({
		queryKey: ['properties', { page, pageSize, search: debouncedSearch, status: statusFilter, isActive: activeFilter }],
		queryFn: () =>
			api.listProperties({
				page,
				pageSize,
				search: debouncedSearch || undefined,
				status: statusFilter || undefined,
				isActive: activeFilter || undefined,
			}),
		placeholderData: (prev) => prev,
	});

	// 全局统计：跟当前筛选无关，给侧栏 + 顶部 metric 用
	const { data: stats = { total: 0, sale: 0, rent: 0, sold: 0 } } = useQuery({
		queryKey: ['stats'],
		queryFn: () => api.getStats(),
		staleTime: 60_000,
	});

	const properties = data?.properties ?? [];

	const saveMutation = useMutation({
		mutationFn: async ({
			data,
			videoFile,
			removeVideo,
		}: {
			data: Partial<Property>;
			videoFile: File | null;
			removeVideo: boolean;
		}) => {
			const saved = editing ? await api.updateProperty(editing.id, data) : await api.createProperty(data);
			const id = editing?.id ?? (typeof saved === 'number' ? saved : saved.id);
			if (removeVideo) await api.deletePropertyVideo(id);
			if (videoFile) await api.uploadPropertyVideo(id, videoFile);
			return saved;
		},
		onSuccess: () => {
			toast.success(editing ? '已更新' : '已新增');
			setFormOpen(false);
			setEditing(null);
			qc.invalidateQueries({ queryKey: ['properties'] });
			qc.invalidateQueries({ queryKey: ['stats'] });
		},
		onError: (err) => toast.error('保存失败：' + (err as Error).message),
	});

	const deleteMutation = useMutation({
		mutationFn: async (target: Property | 'batch') => {
			if (target === 'batch') {
				const ids = Array.from(selected);
				return api.batchDelete(ids);
			}
			return api.deleteProperty(target.id);
		},
		onSuccess: () => {
			toast.success('已删除');
			setSelected(new Set());
			setDeleteTarget(null);
			qc.invalidateQueries({ queryKey: ['properties'] });
			qc.invalidateQueries({ queryKey: ['stats'] });
		},
		onError: (err) => toast.error('删除失败：' + (err as Error).message),
	});

	const toggleSelect = (id: number) => {
		setSelected((prev) => {
			const next = new Set(prev);
			next.has(id) ? next.delete(id) : next.add(id);
			return next;
		});
	};
	const toggleSelectAll = (checked: boolean) => {
		setSelected(checked ? new Set(properties.map((p) => p.id)) : new Set());
	};

	const openAdd = () => { setEditing(null); setFormOpen(true); };
	const openEdit = (p: Property) => { setEditing(p); setFormOpen(true); };

	const title = statusFilter
		? statusFilter === '出售' ? '出售中房源'
			: statusFilter === '出租' ? '出租中房源'
			: '已成交房源'
		: '所有房源';

	return (
		<div className="flex min-h-screen">
			<Sidebar
				currentStatus={statusFilter}
				onChange={(s) => setStatusFilter(s)}
				stats={stats}
				onOpenChat={() => setChatOpen(true)}
				open={sidebarOpen}
				onClose={() => setSidebarOpen(false)}
			/>

			<main className="flex-1 flex flex-col min-w-0">
				<Topbar
					title={title}
					total={data?.total ?? 0}
					search={search}
					onSearchChange={setSearch}
					statusFilter={statusFilter}
					onStatusChange={(v) => setStatusFilter(v as StatusFilter)}
					activeFilter={activeFilter}
					onActiveChange={setActiveFilter}
					selectedCount={selected.size}
					onAdd={openAdd}
					onBatchDelete={() => setDeleteTarget('batch')}
					onMenuToggle={() => setSidebarOpen((v) => !v)}
				/>

				<MetricCards
					metrics={[
						{ label: '总房源', value: stats.total, dotClass: 'bg-accent', toneClass: 'border-t-accent bg-green-100/70' },
						{ label: '出售', value: stats.sale, dotClass: 'bg-sale', toneClass: 'border-t-sale bg-orange-100/80' },
						{ label: '出租', value: stats.rent, dotClass: 'bg-rent', toneClass: 'border-t-rent bg-blue-100/80' },
						{ label: '已成交', value: stats.sold, dotClass: 'bg-sold', toneClass: 'border-t-sold bg-zinc-100' },
					]}
				/>

				<PropertyTable
					properties={properties}
					loading={isLoading}
					selected={selected}
					onToggleSelect={toggleSelect}
					onToggleSelectAll={toggleSelectAll}
					onEdit={openEdit}
					onDelete={(p) => setDeleteTarget(p)}
				/>
				<PropertyCards
					properties={properties}
					loading={isLoading}
					selected={selected}
					onToggleSelect={toggleSelect}
					onEdit={openEdit}
					onDelete={(p) => setDeleteTarget(p)}
				/>

				<Pagination
					page={page}
					pageSize={pageSize}
					total={data?.total ?? 0}
					totalPages={data?.totalPages ?? 1}
					onPageChange={setPage}
					onPageSizeChange={setPageSize}
				/>
			</main>

			<PropertyForm
				open={formOpen}
				onOpenChange={(o) => { setFormOpen(o); if (!o) setEditing(null); }}
				property={editing}
				onSubmit={async (data, videoFile, removeVideo) => {
					await saveMutation.mutateAsync({ data, videoFile, removeVideo });
				}}
				saving={saveMutation.isPending}
			/>

			<Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
				<DialogContent className="max-w-sm">
					<DialogHeader>
						<DialogTitle>确认删除</DialogTitle>
					</DialogHeader>
					<DialogBody>
						<p className="text-ink-2 text-[16.5px]">
							{deleteTarget === 'batch'
								? `确定要删除选中的 ${selected.size} 条房源吗？此操作不可撤销。`
								: deleteTarget
								? `确定要删除「${deleteTarget.community_name} ${deleteTarget.room_number}」吗？此操作不可撤销。`
								: ''}
						</p>
					</DialogBody>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteTarget(null)}>取消</Button>
						<Button
							variant="danger"
							onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget)}
							disabled={deleteMutation.isPending}
						>
							{deleteMutation.isPending ? '删除中...' : '删除'}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<ChatWindow open={chatOpen} onClose={() => setChatOpen(false)} />
		</div>
	);
}
